import { useEffect, useMemo, useState, type ChangeEvent, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import type { ProjectCollectionRecord, ProjectStatus } from '@shared/types';
import { subscribeToShellDeleteRequests } from '@renderer/app/events/shell-delete-events';
import { subscribeToShellDomainInvalidations } from '@renderer/app/events/shell-domain-events';
import { InlineQuickAddPanel, createQuickAddContext } from '@renderer/app/quick-add/quick-add-shared';
import { useShellStore } from '@renderer/app/state/shell-store';
import { AviConversationPanel } from '@renderer/domains/avi/avi-conversation-panel';
import { BulkEditorRail } from '@renderer/shared/components/bulk-editor-rail';
import {
  CollectionColumnSettingsModal,
  resolveVisibleCollectionColumns,
  type CollectionColumnConfig
} from '@renderer/shared/components/collection-column-settings';
import {
  CardGridSurface,
  CollectionSelectionToggle,
  CollectionToolbar,
  CollectionViewToggle,
  DataTableSurface,
  EmptyCollectionState
} from '@renderer/shared/components/collection-scaffold';
import { CollectionTable, type CollectionSortState } from '@renderer/shared/components/collection-table';
import { Icon } from '@renderer/shared/components/icons';
import { SplitWorkspaceLayout } from '@renderer/shared/components/split-workspace';
import {
  FilterChip,
  IconButton,
  PanelCard,
  PanelMenuButton,
  ProgressBar,
  OverflowMenuButton,
  SectionHeader,
  SelectField,
  StatusBadge,
  TextField,
  ToolbarGroup
} from '@renderer/shared/components/primitives';
import { useI18n } from '@renderer/shared/i18n';
import { getAssetPreviewUrl } from '@renderer/shared/utils/asset-url';
import { applyBulkProjectPatch, deleteProjects, listProjects } from './projects-repository';
import {
  selectProjectCards,
  selectProjectSelectionItems,
  selectProjectsSummary,
  type ProjectCardViewModel
} from './projects-selectors';
import { useProjectsViewStore, type ProjectsSort } from './projects-view-store';

type PatchMode = 'unchanged' | 'clear' | 'set';
type SortDirection = 'asc' | 'desc';

function formatDateInputValue(value: string | null): string {
  return value ? value.slice(0, 10) : '';
}

function toIsoDate(value: string): string {
  return `${value}T12:00:00.000Z`;
}

function projectSortToColumnId(sort: ProjectsSort): string | null {
  switch (sort) {
    case 'deadline':
      return 'deadline';
    case 'name':
      return 'name';
    default:
      return null;
  }
}

function projectColumnIdToSort(columnId: string): ProjectsSort | null {
  switch (columnId) {
    case 'deadline':
      return 'deadline';
    case 'name':
      return 'name';
    default:
      return null;
  }
}

function getDefaultProjectSortDirection(sort: ProjectsSort): SortDirection {
  return sort === 'name' ? 'asc' : 'desc';
}

export function ProjectsScreen(): JSX.Element {
  const { locale, t } = useI18n();
  const preferences = useShellStore((state) => state.preferences);
  const savePreferences = useShellStore((state) => state.savePreferences);
  const [collection, setCollection] = useState<ProjectCollectionRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);
  const {
    customerFilter,
    selectionSession,
    setCustomerFilter,
    setStatusFilter,
    setSort,
    setViewMode,
    sort,
    statusFilter,
    syncSelectionQuery,
    toggleSelection,
    removeSelectedId,
    clearSelection,
    updateBulkDraft,
    resetBulkDraft
  } = useProjectsViewStore();
  const [sortDirection, setSortDirection] = useState<SortDirection>(getDefaultProjectSortDirection(sort));

  useEffect(() => {
    let active = true;

    setError(null);
    void listProjects()
      .then((nextCollection) => {
        if (active) {
          setCollection(nextCollection);
        }
      })
      .catch((nextError) => {
        if (active) {
          setError(nextError instanceof Error ? nextError.message : 'Projects unavailable.');
        }
      });

    return () => {
      active = false;
    };
  }, [reloadToken]);

  useEffect(() => {
    return () => {
      clearSelection();
      resetBulkDraft();
    };
  }, [clearSelection, resetBulkDraft]);

  useEffect(
    () =>
      subscribeToShellDomainInvalidations((detail) => {
        if (detail.domain === 'projects') {
          setReloadToken((value) => value + 1);
        }
      }),
    []
  );

  const summary = useMemo(() => (collection ? selectProjectsSummary(collection) : null), [collection]);
  const cards = useMemo(() => {
    if (!collection) {
      return [];
    }

    return selectProjectCards(collection, {
      locale,
      statusFilter,
      customerFilter,
      sort,
      sortDirection,
      t
    });
  }, [collection, customerFilter, locale, sort, sortDirection, statusFilter, t]);
  const projectColumns = useMemo<CollectionColumnConfig[]>(
    () => [
      { id: 'selection', label: '', className: 'av-collection-table__selection-cell', locked: true },
      { id: 'name', label: t('projects.table.name'), sortable: true, locked: true },
      { id: 'customer', label: t('projects.table.customer') },
      { id: 'status', label: t('projects.table.status') },
      { id: 'progress', label: t('projects.table.progress') },
      { id: 'deadline', label: t('projects.table.deadline'), sortable: true },
      { id: 'actions', label: '', align: 'right', locked: true }
    ],
    [t]
  );
  const visibleProjectColumns = useMemo(
    () => resolveVisibleCollectionColumns(projectColumns, preferences.collectionColumnVisibility.projects),
    [preferences.collectionColumnVisibility.projects, projectColumns]
  );
  const selectionItems = useMemo(() => (collection ? selectProjectSelectionItems(collection) : []), [collection]);
  const projectsById = useMemo(
    () => new Map((collection?.projects ?? []).map((project) => [project.id, project])),
    [collection]
  );
  const visibleIds = useMemo(() => cards.map((card) => card.id), [cards]);
  const visibleIdsKey = visibleIds.join('|');
  const queryKey = `${statusFilter}|${customerFilter}|${sort}|${sortDirection}|${preferences.projectsViewMode}`;

  useEffect(() => {
    syncSelectionQuery(queryKey, visibleIds);
  }, [queryKey, syncSelectionQuery, visibleIds, visibleIdsKey]);

  async function handleDeleteSelected(projectIds: string[]): Promise<void> {
    if (projectIds.length === 0) {
      return;
    }

    setMutationError(null);

    try {
      const nextCollection = await deleteProjects({ ids: projectIds });
      setCollection(nextCollection);
      clearSelection();
      resetBulkDraft();
    } catch (nextError) {
      setMutationError(nextError instanceof Error ? nextError.message : 'Project delete failed.');
    }
  }

  useEffect(
    () =>
      subscribeToShellDeleteRequests((detail) => {
        if (detail.domain !== 'projects' || detail.ids.length === 0) {
          return;
        }

        void handleDeleteSelected(detail.ids);
      }),
    [clearSelection, resetBulkDraft]
  );

  if (error) {
    return (
      <div className="av-page">
        <EmptyCollectionState title={t('projects.title')} description={error} />
      </div>
    );
  }

  if (!collection || !summary) {
    return <div className="av-page" />;
  }

  const selectedItems = selectionSession.selectedIds
    .map((selectedId) => selectionItems.find((item) => item.id === selectedId) ?? null)
    .filter((item): item is NonNullable<typeof item> => item !== null);
  const tagsMode =
    selectionSession.bulkDraft.tags.mode === 'set'
      ? selectionSession.bulkDraft.tags.value.length === 0
        ? 'clear'
        : 'set'
      : 'unchanged';
  const customerOptions = collection.customerSummaries.map((customer) => ({
    value: customer.id,
    label: customer.name
  }));
  const quickAddContext = createQuickAddContext({
    customers: collection.customerSummaries,
    projects: collection.projects,
    t
  });
  const hasSelection = selectedItems.length > 0;
  const hasBulkDraftChanges = Object.values(selectionSession.bulkDraft).some((patch) => patch.mode !== 'unchanged');
  const isBulkDraftValid =
    (selectionSession.bulkDraft.assigneeName.mode !== 'set' || selectionSession.bulkDraft.assigneeName.value.trim().length > 0) &&
    (selectionSession.bulkDraft.deadlineAt.mode !== 'set' || selectionSession.bulkDraft.deadlineAt.value.length > 0);
  const tableSortColumnId = projectSortToColumnId(sort);
  const tableSortState: CollectionSortState | undefined = tableSortColumnId
    ? {
        columnId: tableSortColumnId,
        direction: sortDirection
      }
    : undefined;

  function handleSelectProject(projectId: string, event: MouseEvent<HTMLButtonElement>): void {
    event.preventDefault();
    event.stopPropagation();
    toggleSelection(projectId, visibleIds, queryKey, {
      additive: true,
      range: event.shiftKey
    });
  }

  async function handleApplyBulkChanges(): Promise<void> {
    if (!hasSelection || !hasBulkDraftChanges || !isBulkDraftValid) {
      return;
    }

    setMutationError(null);

    try {
      const nextCollection = await applyBulkProjectPatch({
        projectIds: selectionSession.selectedIds,
        ...selectionSession.bulkDraft
      });

      setCollection(nextCollection);
      clearSelection();
      resetBulkDraft();
    } catch (nextError) {
      setMutationError(nextError instanceof Error ? nextError.message : 'Project update failed.');
    }
  }

  function handleTableSortChange(nextSortState: CollectionSortState): void {
    const nextSort = projectColumnIdToSort(nextSortState.columnId);

    if (!nextSort) {
      return;
    }

    setSort(nextSort);
    setSortDirection(nextSortState.direction);
  }

  function handleSortSelectChange(nextSort: ProjectsSort): void {
    setSort(nextSort);
    setSortDirection(getDefaultProjectSortDirection(nextSort));
  }

  function handleVisibleColumnIdsChange(columnIds: string[]): void {
    void savePreferences({
      collectionColumnVisibility: {
        ...preferences.collectionColumnVisibility,
        projects: columnIds
      }
    });
  }

  function handleResetVisibleColumns(): void {
    const nextVisibility = { ...preferences.collectionColumnVisibility };
    delete nextVisibility.projects;
    void savePreferences({ collectionColumnVisibility: nextVisibility });
  }

  function renderProjectCell(card: ProjectCardViewModel, columnId: string): JSX.Element {
    const selected = selectionSession.selectedIds.includes(card.id);
    const project = projectsById.get(card.id);
    const selectionImageSrc = getAssetPreviewUrl(project?.iconAssetId);

    switch (columnId) {
      case 'selection':
        return (
          <td key={columnId} className="av-collection-table__cell av-collection-table__selection-cell">
            <CollectionSelectionToggle
              selected={selected}
              icon="wordpress"
              imageSrc={selectionImageSrc}
              label={card.title}
              onClick={(event) => handleSelectProject(card.id, event)}
            />
          </td>
        );
      case 'name':
        return (
          <td key={columnId} className="av-collection-table__cell">
            <div className="av-collection-table__identity-copy">
              <strong>
                <Link className="av-inline-link" to={`/projects/${card.id}`}>
                  <span>{card.title}</span>
                </Link>
              </strong>
              <div className="av-inline-metadata">
                {card.tags.map((tag) => (
                  <span key={tag} className="av-tag">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </td>
        );
      case 'customer':
        return (
          <td key={columnId} className="av-collection-table__cell">
            <div className="av-summary-stack">
              <strong>{card.customerName}</strong>
              <span>{card.contactName ?? '-'}</span>
            </div>
          </td>
        );
      case 'status':
        return (
          <td key={columnId} className="av-collection-table__cell">
            <div className="av-summary-stack">
              <StatusBadge label={card.statusLabel} tone={card.statusTone} />
              <span>{card.activityLabel}</span>
            </div>
          </td>
        );
      case 'progress':
        return (
          <td key={columnId} className="av-collection-table__cell">
            {card.progressPercent === null ? (
              <span>-</span>
            ) : (
              <div className="av-project-card__progress">
                <ProgressBar tone={card.progressTone} value={card.progressPercent} />
                <small>
                  {t('projects.card.progress')} {card.progressPercent}%
                </small>
              </div>
            )}
          </td>
        );
      case 'deadline':
        return (
          <td key={columnId} className="av-collection-table__cell">
            <div className="av-project-card__footer-right">
              <span>{card.launchedLabel ?? card.deadlineLabel}</span>
              <Link className="av-inline-link" to={`/projects/${card.id}`}>
                {t('common.openDetails')}
                <Icon name="chevron" />
              </Link>
            </div>
          </td>
        );
      case 'actions':
        return (
          <td key={columnId} className="av-collection-table__cell is-right">
            <OverflowMenuButton
              onClick={(event) => {
                event.stopPropagation();
              }}
            />
          </td>
        );
      default:
        return <td key={columnId} className="av-collection-table__cell" />;
    }
  }

  function handleDeadlineModeChange(event: ChangeEvent<HTMLSelectElement>): void {
    const mode = event.target.value as PatchMode;

    if (mode === 'set') {
      updateBulkDraft({ deadlineAt: { mode: 'set', value: new Date().toISOString() } });
      return;
    }

    updateBulkDraft({ deadlineAt: { mode } });
  }

  function handleAssigneeModeChange(event: ChangeEvent<HTMLSelectElement>): void {
    const mode = event.target.value as PatchMode;

    if (mode === 'set') {
      updateBulkDraft({ assigneeName: { mode: 'set', value: '' } });
      return;
    }

    updateBulkDraft({ assigneeName: { mode } });
  }

  function handleTagsModeChange(event: ChangeEvent<HTMLSelectElement>): void {
    const mode = event.target.value as PatchMode;

    if (mode === 'set') {
      updateBulkDraft({ tags: { mode: 'set', value: [] } });
      return;
    }

    if (mode === 'clear') {
      updateBulkDraft({ tags: { mode: 'set', value: [] } });
      return;
    }

    updateBulkDraft({ tags: { mode: 'unchanged' } });
  }

  return (
    <div className="av-page">
      <SectionHeader
        title={t('projects.title')}
        subtitle={`${t('projects.summary.active', { count: summary.activeCount })} • ${t('projects.summary.archived', {
          count: summary.archivedCount
        })}`}
      />

      <InlineQuickAddPanel domain="projects" context={quickAddContext} onCreated={async () => setCollection(await listProjects())} />

      <CollectionToolbar
        primary={
          <ToolbarGroup>
            {(['all', 'active', 'in_progress', 'pending', 'complete'] as const).map((filter) => (
              <FilterChip
                key={filter}
                active={statusFilter === filter}
                icon={filter === 'all' ? 'projects' : filter === 'pending' ? 'timer' : 'work'}
                label={t(`projects.filters.${filter}`)}
                onClick={() => setStatusFilter(filter)}
              />
            ))}
          </ToolbarGroup>
        }
        secondary={
          <ToolbarGroup>
            <SelectField
              options={[
                { value: 'all', label: t('projects.filters.allCustomers') },
                ...customerOptions
              ]}
              value={customerFilter}
              onChange={(event) => setCustomerFilter(event.target.value)}
            />
            <SelectField
              options={[
                { value: 'recent', label: t('projects.sort.recent') },
                { value: 'deadline', label: t('projects.sort.deadline') },
                { value: 'name', label: t('projects.sort.name') }
              ]}
              value={sort}
              onChange={(event) => handleSortSelectChange(event.target.value as ProjectsSort)}
            />
            <CollectionViewToggle
              options={[
                { value: 'list', label: t('common.view.list'), icon: 'list' },
                { value: 'grid', label: t('common.view.grid'), icon: 'grid' }
              ]}
              value={preferences.projectsViewMode}
              onChange={(value) => void setViewMode(value)}
            />
            <IconButton
              icon="settings"
              label={t('common.columns.settingsButton')}
              onClick={() => setColumnSettingsOpen(true)}
            />
          </ToolbarGroup>
        }
      />

      {mutationError ? <div className="av-inline-error">{mutationError}</div> : null}

      <SplitWorkspaceLayout
        railMode="fixed"
        main={
          <>
            {cards.length === 0 ? (
              <EmptyCollectionState title={t('projects.title')} description={t('common.noResults')} />
            ) : preferences.projectsViewMode === 'grid' ? (
              <CardGridSurface mode="grid">
                {cards.map((card) => {
                  const selected = selectionSession.selectedIds.includes(card.id);
                  const project = projectsById.get(card.id);
                  const selectionImageSrc = getAssetPreviewUrl(project?.iconAssetId);

                  return (
                    <PanelCard
                      key={card.id}
                      actions={
                        <PanelMenuButton
                          onClick={(event) => {
                            event.stopPropagation();
                          }}
                        />
                      }
                      className={['av-project-card', selected ? 'is-selected' : ''].filter(Boolean).join(' ')}
                    >
                      <div className="av-project-card__header">
                        <div className="av-project-card__identity">
                          <div className="av-project-card__icon-wrap">
                            <CollectionSelectionToggle
                              selected={selected}
                              className="av-card-selection-toggle"
                              icon="wordpress"
                              imageSrc={selectionImageSrc}
                              label={card.title}
                              data-testid="av-card-selection-toggle"
                              onClick={(event) => handleSelectProject(card.id, event)}
                            />
                          </div>
                          <div>
                            <h2>
                              <Link
                                className="av-inline-link"
                                to={`/projects/${card.id}`}
                                onClick={(event) => {
                                  event.stopPropagation();
                                }}
                              >
                                <span>{card.title}</span>
                              </Link>
                            </h2>
                            <div className="av-project-card__meta">
                              {card.tags.map((tag) => (
                                <span key={tag} className="av-tag">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="av-project-card__status-row">
                        <StatusBadge label={card.statusLabel} tone={card.statusTone} />
                        <span>{card.activityLabel}</span>
                      </div>

                      {card.progressPercent !== null ? (
                        <div className="av-project-card__progress">
                          <ProgressBar tone={card.progressTone} value={card.progressPercent} />
                          <small>
                            {t('projects.card.progress')} {card.progressPercent}%
                          </small>
                        </div>
                      ) : null}

                      <div className="av-project-card__footer">
                        <div>
                          <strong>{card.customerName}</strong>
                          {card.contactName ? <span> • {card.contactName}</span> : null}
                        </div>
                        <div className="av-project-card__footer-right">
                          <span>{card.launchedLabel ?? card.deadlineLabel}</span>
                          <Link
                            className="av-inline-link"
                            to={`/projects/${card.id}`}
                            onClick={(event) => {
                              event.stopPropagation();
                            }}
                          >
                            {t('common.openDetails')}
                            <Icon name="chevron" />
                          </Link>
                        </div>
                      </div>
                    </PanelCard>
                  );
                })}
              </CardGridSurface>
            ) : (
              <DataTableSurface>
                <CollectionTable
                  ariaLabel={t('projects.title')}
                  columns={visibleProjectColumns}
                  sortState={tableSortState}
                  onSortChange={handleTableSortChange}
                >
                  {cards.map((card) => {
                    const selected = selectionSession.selectedIds.includes(card.id);

                    return (
                      <tr
                        key={card.id}
                        className={['av-collection-table__row', selected ? 'is-selected' : ''].filter(Boolean).join(' ')}
                      >
                        {visibleProjectColumns.map((column) => renderProjectCell(card, column.id))}
                      </tr>
                    );
                  })}
                </CollectionTable>
              </DataTableSurface>
            )}

            <PanelCard className="av-avi-panel" title={t('projects.avi.title')}>
              <AviConversationPanel
                contextLabel={(() => {
                  const firstSelectedId = selectionSession.selectedIds[0] ?? null;
                  const selectedCard = firstSelectedId ? cards.find((card) => card.id === firstSelectedId) ?? null : null;
                  return selectedCard ? `${selectedCard.title} • ${selectedCard.customerName}` : null;
                })()}
              />
            </PanelCard>
          </>
        }
        rail={
          hasSelection ? (
            <BulkEditorRail
              title={t('projects.bulk.title', { count: selectedItems.length })}
              hiddenSelectedCount={selectionSession.hiddenSelectedCount}
              items={selectedItems.map((item) => ({
                id: item.id,
                icon: 'projects',
                label: item.label,
                subtitle: item.subtitle
              }))}
              onClose={() => {
                clearSelection();
                resetBulkDraft();
              }}
              onRemoveItem={(itemId) => removeSelectedId(itemId, visibleIds)}
              destructiveAction={{
                label: t('shell.delete'),
                onClick: () => void handleDeleteSelected(selectionSession.selectedIds)
              }}
              primaryAction={{
                label: t('common.applyChanges'),
                disabled: !hasBulkDraftChanges || !isBulkDraftValid,
                onClick: () => void handleApplyBulkChanges()
              }}
              secondaryAction={{
                label: t('common.cancel'),
                onClick: () => {
                  clearSelection();
                  resetBulkDraft();
                }
              }}
            >
              <div className="av-bulk-editor-form">
                <label className="av-form-field">
                  <span>{t('projects.bulk.fields.status')}</span>
                  <SelectField
                    options={[
                      { value: 'unchanged', label: t('common.leaveUnchanged') },
                      { value: 'active', label: t('enums.projectStatus.active') },
                      { value: 'in_progress', label: t('enums.projectStatus.in_progress') },
                      { value: 'pending', label: t('enums.projectStatus.pending') },
                      { value: 'complete', label: t('enums.projectStatus.complete') },
                      { value: 'archived', label: t('enums.projectStatus.archived') }
                    ]}
                    value={selectionSession.bulkDraft.status.mode === 'set' ? selectionSession.bulkDraft.status.value : 'unchanged'}
                    onChange={(event) =>
                      updateBulkDraft({
                        status:
                          event.target.value === 'unchanged'
                            ? { mode: 'unchanged' }
                            : { mode: 'set', value: event.target.value as ProjectStatus }
                      })
                    }
                  />
                </label>

                <label className="av-form-field">
                  <span>{t('projects.bulk.fields.customer')}</span>
                  <SelectField
                    options={[
                      { value: 'unchanged', label: t('common.leaveUnchanged') },
                      ...customerOptions
                    ]}
                    value={selectionSession.bulkDraft.customerId.mode === 'set' ? selectionSession.bulkDraft.customerId.value : 'unchanged'}
                    onChange={(event) =>
                      updateBulkDraft({
                        customerId:
                          event.target.value === 'unchanged'
                            ? { mode: 'unchanged' }
                            : { mode: 'set', value: event.target.value }
                      })
                    }
                  />
                </label>

                <label className="av-form-field">
                  <span>{t('projects.bulk.fields.deadline')}</span>
                  <SelectField
                    options={[
                      { value: 'unchanged', label: t('common.leaveUnchanged') },
                      { value: 'clear', label: t('projects.bulk.clearValue') },
                      { value: 'set', label: t('projects.bulk.setValue') }
                    ]}
                    value={selectionSession.bulkDraft.deadlineAt.mode}
                    onChange={handleDeadlineModeChange}
                  />
                  {selectionSession.bulkDraft.deadlineAt.mode === 'set' ? (
                    <TextField
                      type="date"
                      value={formatDateInputValue(selectionSession.bulkDraft.deadlineAt.value)}
                      onValueChange={(value) =>
                        updateBulkDraft({
                          deadlineAt: { mode: 'set', value: toIsoDate(value) }
                        })
                      }
                    />
                  ) : null}
                </label>

                <label className="av-form-field">
                  <span>{t('projects.bulk.fields.assignee')}</span>
                  <SelectField
                    options={[
                      { value: 'unchanged', label: t('common.leaveUnchanged') },
                      { value: 'clear', label: t('projects.bulk.clearValue') },
                      { value: 'set', label: t('projects.bulk.setValue') }
                    ]}
                    value={selectionSession.bulkDraft.assigneeName.mode}
                    onChange={handleAssigneeModeChange}
                  />
                  {selectionSession.bulkDraft.assigneeName.mode === 'set' ? (
                    <TextField
                      placeholder={t('projects.bulk.assigneePlaceholder')}
                      value={selectionSession.bulkDraft.assigneeName.value}
                      onValueChange={(value) => updateBulkDraft({ assigneeName: { mode: 'set', value } })}
                    />
                  ) : null}
                </label>

                <label className="av-form-field">
                  <span>{t('projects.bulk.fields.tags')}</span>
                  <SelectField
                    options={[
                      { value: 'unchanged', label: t('common.leaveUnchanged') },
                      { value: 'clear', label: t('projects.bulk.clearValue') },
                      { value: 'set', label: t('projects.bulk.setValue') }
                    ]}
                    value={tagsMode}
                    onChange={handleTagsModeChange}
                  />
                  {selectionSession.bulkDraft.tags.mode === 'set' ? (
                    <TextField
                      placeholder={t('projects.bulk.tagsPlaceholder')}
                      value={selectionSession.bulkDraft.tags.value.join(', ')}
                      onValueChange={(value) =>
                        updateBulkDraft({
                          tags: {
                            mode: 'set',
                            value: value
                              .split(',')
                              .map((entry) => entry.trim())
                              .filter(Boolean)
                          }
                        })
                      }
                    />
                  ) : null}
                </label>
              </div>
            </BulkEditorRail>
          ) : null
        }
      />

      <CollectionColumnSettingsModal
        columns={projectColumns}
        domain="projects"
        isOpen={columnSettingsOpen}
        strings={{
          title: t('common.columns.settingsTitle'),
          description: t('common.columns.settingsDescription'),
          reset: t('common.columns.settingsReset'),
          close: t('common.close'),
          locked: t('common.columns.settingsLocked')
        }}
        visibleColumnIds={preferences.collectionColumnVisibility.projects}
        onClose={() => setColumnSettingsOpen(false)}
        onReset={handleResetVisibleColumns}
        onVisibleColumnIdsChange={handleVisibleColumnIdsChange}
      />
    </div>
  );
}
